/*
 * Copyright (c) CIRROM 2021.
 */

import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useState } from "react";
import { green, red } from "@mui/material/colors";

export interface GridTab {
  name: string;
  label: string;
  tooltip?: string;
  totalItems?: number;
  filters: any;
}

/**
 * GenericGridTabs Component
 *
 * This component provides a tab navigation for a grid with suggested changes.
 *
 * @param {object} props - Component props.
 * @param {string} [props.selectedTab] - The initially selected tab.
 * @param {function} props.onSelectionChanged - Callback when tab selection changes.
 * @param {GridTab[]} props.gridTabs - An array of tab configurations.
 * @param {string} props.defaultSelectedTab - The default tab to be selected.
 * @returns {JSX.Element} - Rendered component.
 */
export function GenericGridTabs(
  props: Readonly<{
    selectedTab?: string;
    onSelectionChanged: (filterModel: any, selectedTab: string) => void;
    gridTabs: GridTab[];
    defaultSelectedTab: string;
  }>
) {
  const { onSelectionChanged, selectedTab, gridTabs, defaultSelectedTab } =
    props;
  const [localSelectedTab, setLocalSelectedTab] = useState<string>(
    selectedTab || defaultSelectedTab
  );

  useEffect(() => {
    if (selectedTab && selectedTab !== localSelectedTab) {
      setLocalSelectedTab(selectedTab);
    }
  }, [selectedTab]);

  useEffect(() => {
    if (localSelectedTab) {
      // Call onSelectionChanged callback with filter and selected tab

      onSelectionChanged(
        filterSuggestedChangesGrid(localSelectedTab),
        localSelectedTab
      );
    }
  }, [localSelectedTab]);

  /**
   * filterSuggestedChangesGrid Function
   *
   * Get the filter configuration for the selected tab.
   *
   * @param {string} tabSelection - The selected tab's name.
   * @returns {object} - The filter configuration for the selected tab.
   */
  const filterSuggestedChangesGrid = (tabSelection) => {
    for (let tab of gridTabs) {
      if (tab.name === tabSelection) {
        return tab.filters;
      }
    }
  };

  /**
   * getTabDisplayLabel Function
   *
   * Generate a tab label JSX element with optional total items count.
   *
   * @param {string} tabLabel - The label of the tab.
   * @param {number} [totalItems] - Optional total items count.
   * @returns {JSX.Element} - The tab label JSX element.
   */
  const getTabDisplayLabel = (tabLabel: string, totalItems?: number) => {
    if (totalItems !== undefined) {
      return (
        <>
          <span>{tabLabel}</span>
          <span
            style={{
              fontWeight: "bold",
              verticalAlign: "super",
              fontSize: "75%",
              color: totalItems > 0 ? red[700] : green[700],
              marginLeft: "2px",
            }}
          >
            ({totalItems.toLocaleString()})
          </span>
        </>
      );
    }
    return tabLabel;
  };

  return (
    <Tabs
      value={localSelectedTab}
      onChange={(_event, newValue) => {
        setLocalSelectedTab(newValue);
      }}
      variant={"scrollable"}
      scrollButtons={"auto"}
    >
      {gridTabs.map((gridTab) => {
        return (
          <Tab
            key={gridTab.label}
            value={gridTab.name}
            label={
              gridTab.tooltip ? (
                // Use Tooltip for tab label with tooltip
                <Tooltip title={gridTab.tooltip}>
                  <span>
                    {getTabDisplayLabel(gridTab.label, gridTab.totalItems)}
                  </span>
                </Tooltip>
              ) : (
                <span>
                  {getTabDisplayLabel(gridTab.label, gridTab.totalItems)}
                </span>
              )
            }
          />
        );
      })}
    </Tabs>
  );
}
